import React, { useContext, useState } from "react";
import Button from "Components/button/Button";
import DestinationsModal from "Components/destinationModal/DestinationsModal";
import { alertSuccess } from "Utils/alerts";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";
import { addEmailAlias, plus, trash, edit } from "Icons/index";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import ConfirmModal from "Components/confirmModal/ConfirmModal";
import TranslatedString from "Components/TranslatedString";
import t from "Utils/translation";
import qs from "query-string";
import ButtonIcon from "Components/buttonIcon/ButtonIcon";
import s from "./listAliasEmailAccounts.scss";
import { AuthContext, MediaQueriesContext } from "../../App";
import { useApiMutation, useApiQuery } from "../../hooks";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";

const AliasEmailAccountsScreen = (props) => {
  const { isMobileDevice, isMediaMobile } = useContext(MediaQueriesContext);
  const { getLoggedUser } = useContext(AuthContext);

  const loggedUser = getLoggedUser();
  const domainCode = props.match.params.domainCode || loggedUser?.domain_code;

  const [state, setState] = useState({
    domainCode: domainCode,
    customerCode: props.match.params.customerCode,
    isRefreshing: false,
    timeout: undefined,
    selectedDestinations: [],
    selectedAliasEmail: {},
    selectedAliasEmailCode: undefined,
    modalOpened: false,
    confirmModalOpened: false,
  });
  const location = useLocation();
  const history = useHistory();

  const { mutate: deleteAliasEmailAccount } = useApiMutation("deleteAliasEmailAccount");
  const { mutate: editAliasEmailAccount } = useApiMutation("editAliasEmailAccount");

  const {
    isLoading: areAliasEmailAccountsLoading,
    pagination: paginationInfo,
    refreshData: refreshAliasDomain,
    isRefreshing: aliasEmailAccountsIsRefreshing,
    getData: fetchAliasEmailAccounts,
    data: aliasEmailAccounts,
  } = useApiQuery("getDomainsAliasEmailAccounts", {
    requestParams: domainCode,
    enablePagination: true,
    autoRefresh: {
      shouldRefresh: () => aliasEmailAccounts.some(({ status }) => status === "updating"),
      interval: 4000,
    },
  });

  const closeConfirmationModal = () => {
    setState((prevState) => ({
      ...prevState,
      confirmModalOpened: false,
      selectedAliasEmail: {},
    }));
  };

  const showAliasEmailConfirmationModal = (selectedAliasEmailAccount) => {
    setState((prevState) => ({
      ...prevState,
      confirmModalOpened: true,
      selectedAliasEmail: selectedAliasEmailAccount,
    }));
  };

  const openAddEmailAccountForm = () => {
    history.push(`${location.pathname}/add`);
  };

  const handleUpdateAliasEmail = async (codeEmail, body) => {
    console.log("handleUpdateAliasEmail", state.domainCode, codeEmail, body);

    try {
      await editAliasEmailAccount(state.domainCode, codeEmail, body);
      refreshAliasDomain(state.domainCode);
      alertSuccess(t("alert.destinations.updated"));
      closeUpdateModal();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteAliasEmailAccount = async () => {
    try {
      const codeEmail = state.selectedAliasEmail.code;
      await deleteAliasEmailAccount(state.domainCode, codeEmail);
      refreshAliasDomain(state.domainCode);
    } catch (err) {
      console.error(err);
    }
    closeConfirmationModal();
  };

  const openUpdateModal = (name, code, destinations) => {
    setState((prevState) => ({
      ...prevState,
      modalOpened: true,
      selectedDestinations: destinations,
      selectedAliasEmail: name,
      selectedAliasEmailCode: code,
    }));
  };

  const closeUpdateModal = () => {
    setState((prevState) => ({
      ...prevState,
      selectedDestinations: [],
      selectedAliasEmail: undefined,
      modalOpened: false,
    }));
  };

  let canAddEmailAccount = true;
  if (loggedUser.type === "TeamMember" && !loggedUser.permissions.includes("create_email_accounts")) {
    canAddEmailAccount = false;
  }

  const query = qs.parse(location.search);

  return (
    <div className={s.listAliasEmailAccounts}>
      <div className={s.wrapInfoBtn}>
        {!isMediaMobile && (
          <div className={s.infoText}>
            <TranslatedString>domains.show.desc.aliasEmailAccounts</TranslatedString>
          </div>
        )}
        {canAddEmailAccount && (
          <Button className={s.addBtn} icon={plus} onClick={openAddEmailAccountForm}>
            button.addAliasEmail
          </Button>
        )}
      </div>
      <FullPaginationControls
        className={s.filters}
        paginationInfo={paginationInfo}
        location={location}
        refreshList={() => fetchAliasEmailAccounts(state.domainCode)}
        showListFilter={false}
        isLoading={areAliasEmailAccountsLoading || aliasEmailAccountsIsRefreshing}
        totals={paginationInfo.totals}
      />

      <div className={s.table}>
        <ResponsiveTable
          history={history}
          areRowsLoading={areAliasEmailAccountsLoading && !aliasEmailAccountsIsRefreshing}
          location={location}
          addIcon={addEmailAlias}
          addMsg={t("table.row.add.msg.emailAlias")}
          addDesc={t("table.row.add.desc.emailAlias")}
          headers={[
            {
              name: t("table.header.status"),
              size: isMobileDevice ? "2rem" : "4rem",
              center: true,
            },
            {
              name: t("table.header.alias"),
              key: "name",
              sortable: true,
              activeSorting: query.sort_field === "name" && query.sort,
            },
            { name: t("table.header.code"), size: "6rem", hideOnMobile: true },
            { name: t("table.header.destinations"), hideOnMobile: true },
            { size: "3rem", center: true },
          ]}
          rows={aliasEmailAccounts.map((em) => {
            return (
              <tr key={em.code}>
                <td data-label={t("table.header.status")}>
                  <DomainStatusBadge status={em.status} statusDetail={em.status_detail} isHealthy />
                </td>
                <td data-label={t("table.header.alias")} className="textOverflow">
                  <a onClick={() => openUpdateModal(em.name, em.code, em.destinations)}>
                    {em.name + "@" + em.domain_name}
                  </a>
                </td>
                <td data-label={t("table.header.code")} className="hideOnMobile">
                  {em.code}
                </td>
                <td data-label={t("table.header.destinations")} className="hideOnMobile">
                  {em.destinations.map((d) => (
                    <div key={d}>{d}</div>
                  ))}
                </td>
                <td style={{ display: "flex" }}>
                  {em.status !== "updating" && (
                    <>
                      <ButtonIcon icon={edit} onClick={() => openUpdateModal(em.name, em.code, em.destinations)} />
                      <ButtonIcon icon={trash} onClick={() => showAliasEmailConfirmationModal(em)} />
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        />
      </div>

      <DestinationsModal
        destinations={state.selectedDestinations}
        aliasEmaiName={state.selectedAliasEmail}
        aliasEmailcode={state.selectedAliasEmailCode}
        opened={state.modalOpened}
        updateAliasEmail={handleUpdateAliasEmail}
        handleClose={closeUpdateModal}
      />

      {state.selectedAliasEmail && (
        <ConfirmModal
          title={t("confirmationModal.title.delete.emailAlias")}
          text={
            <TranslatedString subs={{ emailAliasName: state.selectedAliasEmail.name }}>
              confirmationModal.text.delete.emailAlias
            </TranslatedString>
          }
          textButton={`button.delete`}
          opened={state.confirmModalOpened}
          action={handleDeleteAliasEmailAccount}
          handleClose={closeConfirmationModal}
        />
      )}
    </div>
  );
};

export default AliasEmailAccountsScreen;
